import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Download, FileText, Loader2, Paperclip } from 'lucide-react';
import { usePageTitle } from '@/components/layout/page-title';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Hint } from '@/components/ui/hint';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { invoiceXmlUrl, useInvoiceDetail } from './api';
import { AmountPill } from './AmountPill';
import { InvoiceTypeBadge } from './InvoiceTypeBadge';
import { PartyDetails } from './PartyDetails';
import { generateInvoicePdf } from './generateInvoicePdf';
import { formatAccountFull, formatDate, formatDateTime, formatMoney } from './format';
import { deriveLineAmounts, isMarginLine, isMarginOnly, numericRate, type LineAmount } from './lineAmounts';
import type { KsefFooter, KsefInvoiceLine, KsefParty, KsefPayment } from './types';

/**
 * Full-page view of one invoice, reached from the issued/received lists at
 * `/invoices/{issued,received}/:ksefNumber`. Layout follows the document: issuer (seller) and
 * receiver (buyer) side by side at the top, the line items below them, then the remaining
 * amounts / dates / KSeF metadata.
 */
export function InvoiceDetailPage() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const { ksefNumber = '' } = useParams();

  const query = useInvoiceDetail(ksefNumber);
  const detail = query.data;
  const inv = detail?.invoice;
  usePageTitle(inv?.invoiceNumber ?? inv?.ksefNumber ?? '');
  const currency = inv?.currency ?? 'PLN';
  const extra = detail?.extra;
  const payment = extra?.payment ?? null;
  const annotations = extra?.annotations ?? [];
  const correction = extra?.correction ?? null;
  const descriptions = extra?.descriptions ?? [];
  const footer = extra?.footer ?? null;
  const marginProcedure = extra?.marginProcedure ?? null;
  // A wholly-margin invoice discloses no net and no VAT (the tax sits on the seller's margin), so the
  // amounts row collapses to the one figure the document actually states.
  const marginOnly = isMarginOnly(detail?.lines ?? [], inv?.margin ?? false);
  const sectionsGrid = correction
    ? 'grid items-start gap-4 md:grid-cols-2 xl:grid-cols-4'
    : 'grid items-start gap-4 md:grid-cols-2 lg:grid-cols-3';

  const [pdfPending, setPdfPending] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const handleGeneratePdf = async () => {
    if (!inv) return;
    setPdfPending(true);
    setPdfLoading(true);
    try {
      await generateInvoicePdf(inv.ksefNumber, () => setPdfLoading(false));
    } catch {
      toast.error(t('invoices.details.pdf_failed'));
    } finally {
      setPdfLoading(false);
      setPdfPending(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {query.isPending ? (
        <Skeleton className="h-72 w-full" />
      ) : query.isError || !inv || !detail ? (
        <Card>
          <CardContent className="py-6 text-sm text-destructive">
            {t('invoices.load_error')}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="flex flex-wrap items-center gap-2 text-2xl font-semibold tracking-tight">
              {/* Hidden on mobile — the top bar carries the number there; the badges stay. */}
              <span className="max-md:hidden">{inv.invoiceNumber ?? inv.ksefNumber}</span>
              <InvoiceTypeBadge inv={inv} />
              {inv.hasAttachment ? (
                <Badge variant="outline" className="gap-1">
                  <Paperclip className="size-3" />
                  {t('invoices.details.has_attachment')}
                </Badge>
              ) : null}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={handleGeneratePdf}
                disabled={pdfPending}
              >
                {pdfLoading ? (
                  <Loader2 data-icon="inline-start" className="animate-spin" />
                ) : (
                  <FileText data-icon="inline-start" />
                )}
                {t('invoices.details.generate_pdf')}
              </Button>
              <Button asChild variant="outline">
                <a href={invoiceXmlUrl(inv.ksefNumber)} download={`${inv.ksefNumber}.xml`}>
                  <Download data-icon="inline-start" />
                  {t('invoices.details.download_xml')}
                </a>
              </Button>
            </div>
          </div>

          <ScrollArea className="cn-vertical-scroll -mr-3 mt-6 min-h-0 flex-1">
            <div className="space-y-6 pr-3">
              <div
                className={
                  payment ? 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3' : 'grid gap-4 md:grid-cols-2'
                }
              >
                <PartyCard
                  label={t('invoices.details.seller')}
                  party={detail.seller}
                  fallbackName={inv.sellerName}
                  fallbackId={inv.sellerNip}
                />
                <PartyCard
                  label={t('invoices.details.buyer')}
                  party={detail.buyer}
                  fallbackName={inv.buyerName}
                  fallbackId={inv.buyerIdentifier}
                />
                {payment ? <PaymentCard payment={payment} lng={lng} /> : null}
              </div>

              <Card>
                <CardContent>
                  {detail.lines.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('invoices.details.lines.empty')}
                    </p>
                  ) : (
                    <LinesTable
                      lines={detail.lines}
                      lng={lng}
                      currency={currency}
                      margin={inv.margin ?? false}
                    />
                  )}
                </CardContent>
              </Card>

              <div className="flex flex-wrap justify-end gap-2">
                {marginOnly ? (
                  <AmountPill
                    label={t('invoices.margin.total_due')}
                    value={
                      inv.grossAmount != null ? formatMoney(lng, inv.grossAmount, currency) : '—'
                    }
                  />
                ) : (
                  <>
                    <AmountPill
                      label={t('invoices.details.net')}
                      value={inv.netAmount != null ? formatMoney(lng, inv.netAmount, currency) : '—'}
                    />
                    <AmountPill
                      label={t('invoices.details.vat')}
                      value={inv.vatAmount != null ? formatMoney(lng, inv.vatAmount, currency) : '—'}
                    />
                    <AmountPill
                      label={t('invoices.details.gross')}
                      value={
                        inv.grossAmount != null ? formatMoney(lng, inv.grossAmount, currency) : '—'
                      }
                    />
                  </>
                )}
              </div>

              {annotations.length > 0 ? (
                <section className="space-y-2">
                  <SectionTitle>{t('invoices.details.section.annotations')}</SectionTitle>
                  <div className="flex flex-wrap gap-2">
                    {annotations.map((a) => (
                      <Badge key={a} variant="secondary">
                        {/* `PMarzy` names one of four procedures; say which rather than just "margin
                            scheme" — art. 119 travel and the art. 120 goods categories are taxed
                            differently and the badge is the only place the choice surfaces. */}
                        {a === 'margin' && marginProcedure
                          ? `${t('invoices.details.annotation.margin')} — ${t(
                              `invoices.margin.procedures.${marginProcedure}`,
                            )}`
                          : t(`invoices.details.annotation.${a}`, { defaultValue: a })}
                      </Badge>
                    ))}
                  </div>
                </section>
              ) : null}

              {extra?.exemptionLegalBasis ? (
                <Section title={t('invoices.details.section.exemption')}>
                  <Field label={t('invoices.details.exemption_basis')}>
                    {extra.exemptionLegalBasis}
                  </Field>
                </Section>
              ) : null}

              {descriptions.length > 0 ? (
                <Section title={t('invoices.details.section.descriptions')}>
                  {descriptions.map((d, i) => (
                    <Field key={i} label={d.key ?? '—'}>
                      {d.value ?? '—'}
                    </Field>
                  ))}
                </Section>
              ) : null}

              {footer ? <FooterSection footer={footer} /> : null}

              <div className={sectionsGrid}>
                <Section title={t('invoices.details.section.dates')}>
                  {inv.issueDate ? (
                    <Field label={t('invoices.details.issued')}>
                      {formatDate(lng, inv.issueDate)}
                    </Field>
                  ) : null}
                  {extra?.saleDate ? (
                    <Field label={t('invoices.details.sale_date')}>
                      {formatDate(lng, extra.saleDate)}
                    </Field>
                  ) : null}
                  {extra?.salePeriodFrom || extra?.salePeriodTo ? (
                    <Field label={t('invoices.details.sale_period')}>
                      {[extra.salePeriodFrom, extra.salePeriodTo]
                        .map((d) => (d ? formatDate(lng, d) : '—'))
                        .join(' – ')}
                    </Field>
                  ) : null}
                </Section>

                {correction ? (
                  <Section title={t('invoices.details.correction')}>
                    {correction.reason ? (
                      <Field label={t('invoices.details.correction_reason')}>
                        {correction.reason}
                      </Field>
                    ) : null}
                    {correction.correctedInvoiceNumber ? (
                      <Field label={t('invoices.details.correction_invoice')}>
                        {correction.correctedInvoiceNumber}
                      </Field>
                    ) : null}
                    {correction.correctedInvoiceDate ? (
                      <Field label={t('invoices.details.correction_date')}>
                        {formatDate(lng, correction.correctedInvoiceDate)}
                      </Field>
                    ) : null}
                  </Section>
                ) : null}

                <Section title={t('invoices.details.section.ksef')}>
                  <Field label={t('invoices.details.ksef_number')}>
                    <span className="font-mono text-xs break-all">{inv.ksefNumber}</span>
                  </Field>
                  {inv.formCode || inv.schemaVersion ? (
                    <Field label={t('invoices.details.schema')}>
                      {[inv.formCode, inv.schemaVersion].filter(Boolean).join(' · ')}
                    </Field>
                  ) : null}
                  {inv.selfInvoicing != null ? (
                    <Field label={t('invoices.details.self_invoicing')}>
                      {inv.selfInvoicing ? t('common.yes') : t('common.no')}
                    </Field>
                  ) : null}
                  {inv.invoiceHash ? (
                    <Field label={t('invoices.details.invoice_hash')}>
                      <span className="font-mono text-xs break-all">{inv.invoiceHash}</span>
                    </Field>
                  ) : null}
                  {inv.correctedInvoiceHash ? (
                    <Field label={t('invoices.details.corrected_invoice_hash')}>
                      <span className="font-mono text-xs break-all">
                        {inv.correctedInvoiceHash}
                      </span>
                    </Field>
                  ) : null}
                </Section>

                <Section title={t('invoices.details.section.processing')}>
                  {inv.invoicingDate ? (
                    <Field label={t('invoices.details.invoicing_date')}>
                      {formatDateTime(lng, inv.invoicingDate)}
                    </Field>
                  ) : null}
                  {inv.acquisitionDate ? (
                    <Field label={t('invoices.details.acquisition_date')}>
                      {formatDateTime(lng, inv.acquisitionDate)}
                    </Field>
                  ) : null}
                  {inv.permanentStorageDate ? (
                    <Field label={t('invoices.details.permanent_storage_date')}>
                      {formatDateTime(lng, inv.permanentStorageDate)}
                    </Field>
                  ) : null}
                  {inv.invoicingMode ? (
                    <Field label={t('invoices.details.invoicing_mode')}>{inv.invoicingMode}</Field>
                  ) : null}
                </Section>
              </div>
            </div>
          </ScrollArea>
        </>
      )}
    </div>
  );
}

/** One party (issuer or receiver) with all the data we hold: name, tax id, address, contact. */
function PartyCard({
  label,
  party,
  fallbackName,
  fallbackId,
}: {
  label: string;
  party?: KsefParty | null;
  fallbackName?: string | null;
  fallbackId?: string | null;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <PartyDetails
          party={{
            name: party?.name ?? fallbackName,
            taxId: party?.nip ?? fallbackId,
            addressLines: [party?.addressLine1, party?.addressLine2],
            countryCode: party?.countryCode,
            email: party?.email,
            phone: party?.phone,
          }}
        />
      </CardContent>
    </Card>
  );
}

/** Payment terms card: method, due date, settled status, and bank account(s). */
function PaymentCard({ payment, lng }: { payment: KsefPayment; lng: string }) {
  const { t } = useTranslation();
  const terms = payment.terms ?? [];
  const accounts = payment.bankAccounts ?? [];
  const method = payment.method
    ? t(`invoices.details.payment.methods.${payment.method}`, { defaultValue: payment.method })
    : payment.otherMethod
      ? t('invoices.details.payment.method_other')
      : null;
  const paidValue = payment.paidDate
    ? formatDate(lng, payment.paidDate)
    : payment.paid
      ? t('common.yes')
      : payment.partiallyPaid
        ? t('invoices.details.payment.partially')
        : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>{t('invoices.details.payment.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="divide-y">
          {method ? <Field label={t('invoices.details.payment.method')}>{method}</Field> : null}
          {terms.map((term, i) => (
            <Field key={i} label={t('invoices.details.payment.due_date')}>
              {[term.date ? formatDate(lng, term.date) : null, term.description]
                .filter(Boolean)
                .join(' · ') || '—'}
            </Field>
          ))}
          {paidValue ? <Field label={t('invoices.details.payment.paid')}>{paidValue}</Field> : null}
          {payment.description ? (
            <Field label={t('invoices.details.payment.note')}>{payment.description}</Field>
          ) : null}
          {accounts.map((account, i) => (
            <Fragment key={i}>
              {account.number ? (
                <Field
                  label={t(
                    account.factor
                      ? 'invoices.details.payment.factor'
                      : 'invoices.details.payment.account',
                  )}
                >
                  {formatAccountFull(account.number)}
                </Field>
              ) : null}
              {account.bankName ? (
                <Field label={t('invoices.details.payment.bank')}>{account.bankName}</Field>
              ) : null}
              {account.swift ? (
                <Field label={t('invoices.details.payment.swift')}>{account.swift}</Field>
              ) : null}
            </Fragment>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

/**
 * The invoice footer (`Stopka`): the free-text notes (which can run to several thousand characters,
 * so they are clamped with a show-more toggle) and the issuer's registry identifiers.
 */
function FooterSection({ footer }: { footer: KsefFooter }) {
  const { t } = useTranslation();
  const notes = footer.notes ?? [];
  const registries = footer.registries ?? [];

  return (
    <section className="space-y-2">
      <SectionTitle>{t('invoices.details.section.footer')}</SectionTitle>
      {notes.length > 0 ? <FooterNotes notes={notes} /> : null}
      {registries.map((r, i) => (
        <dl key={i} className="divide-y rounded-md border">
          {r.fullName ? (
            <Field label={t('invoices.details.footer.full_name')}>{r.fullName}</Field>
          ) : null}
          {r.krs ? <Field label="KRS">{r.krs}</Field> : null}
          {r.regon ? <Field label="REGON">{r.regon}</Field> : null}
          {r.bdo ? <Field label="BDO">{r.bdo}</Field> : null}
        </dl>
      ))}
    </section>
  );
}

/** Free-text footer notes, clamped to a few lines with a show-more toggle when they run long. */
function FooterNotes({ notes }: { notes: string[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const text = notes.join('\n\n');
  const clampable = text.length > 280;

  return (
    <div className="rounded-md border p-3">
      <p
        className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${
          clampable && !expanded ? 'line-clamp-4' : ''
        }`}
      >
        {text}
      </p>
      {clampable ? (
        <Button
          variant="link"
          className="mt-1 h-auto p-0 text-xs"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? t('invoices.details.footer.show_less')
            : t('invoices.details.footer.show_more')}
        </Button>
      ) : null}
    </div>
  );
}

function LinesTable({
  lines,
  lng,
  currency,
  margin,
}: {
  lines: KsefInvoiceLine[];
  lng: string;
  currency: string;
  /** The invoice-level `PMarzy` flag; the amount shape is decided per line (see {@link isMarginLine}). */
  margin: boolean;
}) {
  const { t } = useTranslation();
  const qty = (line: KsefInvoiceLine) =>
    line.quantity != null ? `${line.quantity}${line.unit ? ` ${line.unit}` : ''}` : '—';
  const rateLabel = (line: KsefInvoiceLine) =>
    line.vatRate == null ? '—' : numericRate(line) != null ? `${line.vatRate}%` : line.vatRate;

  // A margin-scheme line is priced gross end to end (P_9B / P_11A) and states no net, rate or VAT. When
  // every line is one, the table drops those three columns instead of ruling em dashes down each row;
  // a mixed invoice keeps the full column set, since its rated rows' net and VAT are real.
  const marginOnly = isMarginOnly(lines, margin);
  const columnCount = marginOnly ? 5 : 8;

  // Net (P_11), unit net (P_9A), VAT and gross are each either source-provided, reconstructed by
  // derivation, or unknown. Source values render plain; reconstructed ones are marked with a `≈`
  // and a tooltip naming the formula, so a computed figure never masquerades as what the issuer
  // actually wrote (this is a fiscal document — the distinction is correctness, not polish); unknown
  // stays an em dash.
  const amountCell = (amount: LineAmount): ReactNode => {
    if (amount.value == null) return '—';
    const text = formatMoney(lng, amount.value, currency);
    if (amount.basis == null) return text;
    return (
      <Hint
        side="top"
        content={t('invoices.details.lines.calculated', {
          basis: t(`invoices.details.lines.basis.${amount.basis}`),
        })}
      >
        <button
          type="button"
          className="cursor-help underline decoration-dotted underline-offset-4 outline-none"
        >
          <span aria-hidden className="mr-0.5 text-muted-foreground">
            ≈
          </span>
          {text}
        </button>
      </Hint>
    );
  };

  const numeric = 'px-3 py-2 text-right tabular-nums whitespace-nowrap';

  // A correction (KOR) invoice repeats each position as a before/after pair: FA(3) flags the
  // before-state row with `StanPrzed=1` and leaves the after-state row unmarked (there is no
  // `StanPo`). When any such row is present, split the table into a muted "before" group and an
  // "after" group so the two states read clearly; otherwise render one flat body, exactly as before.
  const row = (line: KsefInvoiceLine, key: string, no: ReactNode, muted: boolean) => {
    const amounts = deriveLineAmounts(line, margin);
    return (
      <tr
        key={key}
        className={
          muted ? 'border-b text-muted-foreground last:border-0' : 'border-b last:border-0'
        }
      >
        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{no}</td>
        <td className="px-3 py-2">
          <span className="whitespace-pre-line">{line.name ?? '—'}</span>
          {/* In a mixed table, mark the margin rows: it is why their net, rate and VAT read as em
              dashes while the neighbouring rows carry all three. */}
          {!marginOnly && isMarginLine(line, margin) ? (
            <Badge variant="secondary" className="ml-2 align-middle font-normal">
              {t('invoices.margin.badge')}
            </Badge>
          ) : null}
        </td>
        <td className={numeric}>{qty(line)}</td>
        {marginOnly ? (
          <>
            <td className={numeric}>{amountCell(amounts.unitGross)}</td>
            <td className={numeric}>{amountCell(amounts.gross)}</td>
          </>
        ) : (
          <>
            <td className={numeric}>{amountCell(amounts.unitNet)}</td>
            <td className={numeric}>{amountCell(amounts.net)}</td>
            <td className={numeric}>{rateLabel(line)}</td>
            <td className={numeric}>{amountCell(amounts.vat)}</td>
            <td className={numeric}>{amountCell(amounts.gross)}</td>
          </>
        )}
      </tr>
    );
  };
  const rows = (list: KsefInvoiceLine[], prefix: string, muted: boolean) =>
    list.map((line, i) => row(line, `${prefix}${line.lineNumber ?? i}`, line.lineNumber ?? i + 1, muted));
  const stateHeader = (label: string) => (
    <tr className="bg-muted/40">
      <td
        colSpan={columnCount}
        className="px-3 py-1.5 text-xs font-medium tracking-wide text-muted-foreground uppercase"
      >
        {label}
      </td>
    </tr>
  );
  const hasStates = lines.some((line) => line.stanPrzed);

  return (
    <ScrollArea className="relative w-full">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="w-px px-3 py-2 text-right font-normal">
              {t('invoices.details.lines.no')}
            </th>
            <th className="w-full px-3 py-2 font-normal">{t('invoices.details.lines.item')}</th>
            <th className="px-3 py-2 text-right font-normal">{t('invoices.details.lines.qty')}</th>
            {marginOnly ? (
              <>
                <th className="px-3 py-2 text-right font-normal">
                  {t('invoices.details.lines.unit_gross')}
                </th>
                <th className="px-3 py-2 text-right font-normal">
                  {t('invoices.details.lines.gross')}
                </th>
              </>
            ) : (
              <>
                <th className="px-3 py-2 text-right font-normal">
                  {t('invoices.details.lines.unit_net')}
                </th>
                <th className="px-3 py-2 text-right font-normal">
                  {t('invoices.details.lines.net')}
                </th>
                <th className="px-3 py-2 text-right font-normal">
                  {t('invoices.details.lines.rate')}
                </th>
                <th className="px-3 py-2 text-right font-normal">
                  {t('invoices.details.lines.vat')}
                </th>
                <th className="px-3 py-2 text-right font-normal">
                  {t('invoices.details.lines.gross')}
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {hasStates ? (
            <>
              {stateHeader(t('invoices.details.lines.state_before'))}
              {rows(
                lines.filter((line) => line.stanPrzed),
                'b-',
                true,
              )}
              {stateHeader(t('invoices.details.lines.state_after'))}
              {rows(
                lines.filter((line) => !line.stanPrzed),
                'a-',
                false,
              )}
            </>
          ) : (
            rows(lines, '', false)
          )}
        </tbody>
      </table>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
      {children}
    </h2>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <SectionTitle>{title}</SectionTitle>
      <dl className="divide-y rounded-md border">{children}</dl>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2 text-sm">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium">{children}</dd>
    </div>
  );
}
